import { DeleteOutlined } from "@ant-design/icons"
import { Button } from "components/Button/Button"
import { ErrorStatus } from "components/ErrorStatus/ErrorStatus"
import { Modal } from "components/Modal/Modal"
import { Tooltip } from "components/Tooltip/Tooltip"
import { Wrap } from "components/Wrap/Wrap"
import { useDisclosure } from "hooks/useDisclosure"
import { Escalation } from "interface/escalation"
import { useDeleteEscalation } from "services/escalations"
import { useUserRole } from "services/users"
import { GENERIC_ERROR_MESSAGE } from "utils/constants"

export function DeleteEscalation({
	escalation: { id, assignee, patientId, createdBy },
	onSuccess,
}: {
	escalation: Escalation
	onSuccess: VoidFunction
}) {
	const deleteEscalation = useDeleteEscalation(patientId, id)
	const deleteDisclose = useDisclosure({
		onClose: deleteEscalation.reset,
	})
	const { data: { id: userId } = {} } = useUserRole()
	const isAssignedToOther = Boolean(assignee && assignee !== createdBy)
	const handleOk = () =>
		deleteEscalation.mutate(undefined, {
			onSuccess: () => {
				deleteDisclose.close()
				onSuccess()
			},
		})

	if (!userId || userId !== createdBy) {
		return null
	}

	return (
		<Wrap
			condition={isAssignedToOther}
			with={(children) => (
				<Tooltip.Dark title="Escalations may not be deleted once assigned to another provider.">
					{children}
				</Tooltip.Dark>
			)}
		>
			<Button
				danger
				disabled={isAssignedToOther}
				aria-label="Delete escalation"
				icon={<DeleteOutlined aria-hidden />}
				onClick={deleteDisclose.open}
			/>

			{deleteDisclose.isOpen && (
				<Modal
					cancelText="Cancel"
					title="Delete escalation"
					okText="Delete escalation"
					open={deleteDisclose.isOpen}
					okButtonProps={{ danger: true }}
					confirmLoading={deleteEscalation.isPending}
					classNames={{ body: "flex flex-col gap-2" }}
					onOk={handleOk}
					onCancel={deleteDisclose.close}
				>
					This will permanently delete the escalation.
					<ErrorStatus show={deleteEscalation.isError}>
						{deleteEscalation.error?.message ?? GENERIC_ERROR_MESSAGE}
					</ErrorStatus>
				</Modal>
			)}
		</Wrap>
	)
}
